import {
  closestCenter,
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { ReactNode } from "react";

import { SortableItem } from "./parts/sortable_list_item";
import { ListAction, SortableListItem } from "./sortable_list.types";

export type SortableListProps = {
  onChange: (sortedList: SortableListItem[], itemId: string, listAction: ListAction) => void;
  onToggle?: (itemId: string, value: boolean) => void;
  renderAction?: (item: SortableListItem) => ReactNode;
  onDragStart?: (event: DragStartEvent) => void;
  strategy: typeof horizontalListSortingStrategy | typeof verticalListSortingStrategy;
  items: SortableListItem[];
  collisionDetection?: CollisionDetection;
  action?: Omit<ListAction, ListAction.Sort>;
  disabled?: boolean;
  classes?: Partial<{
    item: string;
    dragIcon: string;
  }>;
  keyPrefix?: string;
};

export const SortableList = ({
  onChange,
  onToggle,
  renderAction,
  onDragStart,
  strategy,
  items,
  collisionDetection,
  action,
  disabled,
  classes,
  keyPrefix
}: SortableListProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const customCollisionDetection: CollisionDetection = (args) => {
    const collisions = closestCenter(args);

    return collisions.filter((collision) => {
      const overItem = items.find((item) => item.id === collision.id);
      return !overItem?.sortDisabled;
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      const overItem = items.find((item) => item.id === over.id);
      if (overItem?.sortDisabled) {
        return;
      }

      const fixedItemsMap = items.reduce((map, item, index) => {
        if (item.sortDisabled) {
          map.set(index, item);
        }
        return map;
      }, new Map<number, SortableListItem>());

      const movableItems = items.filter((item) => !item.sortDisabled);
      const oldIndex = movableItems.findIndex((item) => item.id === active.id);
      const newIndex = movableItems.findIndex((item) => item.id === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      const sortedMovableItems = arrayMove(movableItems, oldIndex, newIndex);

      const newOrder = [] as SortableListItem[];
      let movableIndex = 0;
      for (let i = 0; i < items.length; i++) {
        const fixedItem = fixedItemsMap.get(i);
        if (fixedItem) {
          newOrder.push(fixedItem);
        } else {
          newOrder.push(sortedMovableItems[movableIndex]);
          movableIndex++;
        }
      }

      onChange(newOrder, String(active.id), ListAction.Sort);
      return newOrder;
    }
  };

  const handleRemove = (itemId: string) => {
    const itemIndex = items.findIndex((item) => item.id === itemId);
    const listCopy = [...items];
    listCopy.splice(itemIndex, 1);
    onChange(listCopy, itemId, ListAction.Removal);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection || customCollisionDetection}
      onDragStart={onDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext strategy={strategy} items={items}>
        {items.map((item) => (
          <SortableItem
            key={`${keyPrefix}-${item.id}`}
            {...item}
            onRemove={handleRemove}
            onToggle={onToggle}
            renderAction={renderAction}
            strategy={strategy}
            action={action}
            disabled={disabled}
            classes={classes}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};
